import { type Clip, ClipType } from '@air/api/types';

import { type PendingCanvasEditRequest } from '~/components/v2/store/features/canvas/usePendingCanvasEditStore';

export type GetPendingEditInCanvasNodeIdParams = {
  action: PendingCanvasEditRequest['action'];
  /** Parallel to `clips` / `nodeIds`. Convert to GIF aims at the first true entry. */
  canConvertToGif?: boolean[];
  clips: Pick<Clip, 'type'>[];
  nodeIds: string[];
};

type GetFirstMatchingNodeIdParams = {
  clips: Pick<Clip, 'type'>[];
  isMatch: (clip: Pick<Clip, 'type'>, index: number) => boolean;
  nodeIds: string[];
};

const getFirstMatchingNodeId = ({ clips, isMatch, nodeIds }: GetFirstMatchingNodeIdParams): string | undefined => {
  for (let index = 0; index < clips.length; index += 1) {
    const clip = clips[index];
    const nodeId = nodeIds[index];

    if (clip && nodeId && isMatch(clip, index)) {
      return nodeId;
    }
  }

  return undefined;
};

/**
 * Image jobs are per-node. A video-only selection (one clip or many) keeps the video handoff
 * on the first video, except Convert to GIF which aims at the first GIF-eligible clip.
 * In a mixed group, videos still land on the canvas, but the tool aims at the first image.
 * Resize for set only runs on images, so it never falls back to a video node.
 */
export const getPendingEditInCanvasNodeId = ({
  action,
  canConvertToGif,
  clips,
  nodeIds,
}: GetPendingEditInCanvasNodeIdParams): string | undefined => {
  if (action === 'open') {
    return undefined;
  }

  if (action === 'videoConvert') {
    return getFirstMatchingNodeId({
      clips,
      isMatch: (clip, index) => clip.type === ClipType.video && Boolean(canConvertToGif?.[index]),
      nodeIds,
    });
  }

  if (action === 'videoExtend') {
    return getFirstMatchingNodeId({ clips, isMatch: (clip) => clip.type === ClipType.video, nodeIds });
  }

  const [firstNodeId] = nodeIds;
  const isVideoOnly = clips.length > 0 && clips.every((clip) => clip.type === ClipType.video);

  if (isVideoOnly && action !== 'resizeForSet') {
    return firstNodeId;
  }

  return getFirstMatchingNodeId({
    clips,
    isMatch: (clip) => clip.type !== ClipType.video,
    nodeIds,
  });
};
